import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "@/lib/api";
import { fileUrl } from "@/lib/upload";
import { LANDING_DEFAULTS } from "./landingDefaults";
import * as Icons from "lucide-react";
import { ArrowRight, MapPin, Users, ShieldCheck, Sparkles } from "lucide-react";

export const LANDING_ICONS = ["BookOpenCheck", "MessageCircle", "IndianRupee", "Smartphone", "GraduationCap", "ShieldCheck", "CalendarCheck", "Bus", "Library", "Award", "Bell", "FileBadge"];

export function useLanding() {
  const q = useQuery({
    queryKey: ["pub-landing"],
    queryFn: async () => (await axios.get(`${API}/public/landing`)).data,
    retry: 1,
    staleTime: 60_000,
  });
  // Never leave callers without content — fall back to bundled defaults if the
  // API is unreachable (cold backend, offline) so the site still renders.
  return { ...q, data: { ...LANDING_DEFAULTS, ...(q.data || {}) } };
}

export default function Landing() {
  const { data: schools = [], isLoading, isError } = useQuery({
    queryKey: ["pub-schools"],
    queryFn: async () => {
      const { data } = await axios.get(`${API}/public/schools`, { timeout: 90_000 });
      try { localStorage.setItem("ep_schools", JSON.stringify(data)); } catch { /* private mode */ }
      return data;
    },
    retry: 3,
    retryDelay: (n) => Math.min(1000 * 2 ** n, 8000),
    staleTime: 60_000,
    placeholderData: () => {
      try { return JSON.parse(localStorage.getItem("ep_schools") || "null") || undefined; }
      catch { return undefined; }
    },
  });
  const { data: L } = useLanding();

  return (
    <div data-testid="landing-page">
      {/* Hero */}
      <section className="pub-mesh relative overflow-hidden">
        <div className="pub-dots absolute inset-0 opacity-60" />
        <div className="relative mx-auto grid max-w-7xl gap-10 px-5 py-16 md:grid-cols-12 md:py-24">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="md:col-span-7">
            {L.badge && <span className="inline-flex items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700" data-testid="landing-badge"><Sparkles size={13} /> {L.badge}</span>}
            <h1 className="mt-5 text-4xl font-bold font-head tracking-tight sm:text-5xl lg:text-6xl" data-testid="landing-title">
              {L.hero_title} <span className="text-indigo-600">{L.hero_highlight}</span>
            </h1>
            <p className="mt-5 max-w-xl text-base text-slate-600 sm:text-lg">{L.hero_text}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#schools" className="pub-btn" data-testid="hero-browse-btn">Browse Schools <ArrowRight size={16} /></a>
              <Link to="/track" className="pub-btn pub-btn-outline" data-testid="hero-track-btn">Track Application</Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-8 text-sm">
              {[["Schools", schools.length || "—"], ["Students", schools.reduce((a, s) => a + (s.students || 0), 0) || "—"], ["WhatsApp alerts", "Instant"]].map(([k, v]) => (
                <div key={k}><p className="text-2xl font-bold font-head">{v}</p><p className="text-slate-500">{k}</p></div>
              ))}
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }} className="relative md:col-span-5">
            <div className="overflow-hidden rounded-3xl shadow-2xl shadow-indigo-500/20 ring-1 ring-slate-200">
              <img src={fileUrl(L.hero_image)} alt="Students learning" className="h-full w-full object-cover aspect-[4/5]" data-testid="landing-hero-img" />
            </div>
            <div className="pub-card absolute -bottom-6 -left-6 hidden items-center gap-3 p-4 sm:flex">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600"><ShieldCheck size={20} /></div>
              <div><p className="text-sm font-semibold">{L.hero_card_title}</p><p className="text-xs text-slate-500">{L.hero_card_text}</p></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Schools directory */}
      <section id="schools" className="mx-auto max-w-7xl px-5 py-16">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600">School Directory</p>
            <h2 className="mt-1 text-2xl font-semibold font-head sm:text-3xl" data-testid="directory-title">{L.directory_title}</h2>
          </div>
          <p className="text-sm text-slate-500">{L.directory_subtitle}</p>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="school-directory">
          {isLoading && !schools.length && (
            <p className="text-slate-500">Loading schools… <span className="text-slate-400">first visit of the day can take up to a minute while the server wakes.</span></p>
          )}
          {isError && !schools.length && (
            <p className="text-slate-500">Couldn’t reach the server just now. <button onClick={() => window.location.reload()} className="font-medium text-indigo-600 hover:underline">Retry</button></p>
          )}
          {schools.map((s, i) => (
            <motion.div key={s.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}>
              <Link to={`/s/${s.code}`} className="pub-card block overflow-hidden" data-testid={`school-card-${s.code}`}>
                <div className="relative h-36 overflow-hidden">
                  <img src={fileUrl(s.profile.hero_image)} alt={s.name} className="h-full w-full object-cover transition duration-500 hover:scale-105" />
                  {s.logo_url && <img src={fileUrl(s.logo_url)} alt="" className="absolute bottom-3 left-4 h-11 w-11 rounded-xl border-2 border-white object-cover shadow" />}
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-semibold font-head text-lg leading-tight">{s.name}</h3>
                      <p className="mt-1 flex items-center gap-1 text-xs text-slate-500"><MapPin size={12} /> {s.city || s.address}</p>
                    </div>
                    <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-[11px] font-semibold text-indigo-700">{s.code}</span>
                  </div>
                  <p className="mt-3 line-clamp-2 text-sm text-slate-600">{s.profile.tagline}</p>
                  <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
                    <span className="flex items-center gap-1"><Users size={13} /> {s.students} students</span>
                    <span className="flex items-center gap-1 font-semibold text-indigo-600">View portal <ArrowRight size={13} /></span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
          {!isLoading && !schools.length && <p className="text-slate-500">No active schools yet.</p>}
        </div>
      </section>

      {/* Features */}
      <section className="bg-slate-900 text-white">
        <div className="mx-auto max-w-7xl px-5 py-16">
          <p className="text-xs font-semibold uppercase tracking-wider text-cyan-400">Why {L.brand_name}</p>
          <h2 className="mt-1 text-2xl font-semibold font-head sm:text-3xl">{L.features_title}</h2>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="landing-features">
            {L.features.map((f, i) => {
              const I = Icons[f.icon] || Icons.Sparkles;
              return (
                <motion.div key={f.title + i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="rounded-2xl border border-slate-800 bg-slate-800/40 p-5">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-500/20 text-indigo-300"><I size={20} /></div>
                  <h3 className="mt-4 font-semibold font-head">{f.title}</h3>
                  <p className="mt-1.5 text-sm text-slate-400">{f.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
